import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { RootState } from '@core/interfaces/root-state';
import Vue from 'vue';
import flatten from 'flat';
import {
  FlattenObject,
  TranslationCollectionObject,
  TranslationReport,
} from '@dev/interfaces/translation-reports';

export const convertToDotNotation = (object: object): FlattenObject => {
  return flatten.flatten(object);
};

export interface TranslationReportState {
  translationsReady: boolean;
  allMessages: TranslationCollectionObject;
  potentiallyUnusedTranslations: TranslationCollectionObject;
  usedTranslations: TranslationReport;
  missingTranslations: TranslationReport;
}

const state: TranslationReportState = {
  translationsReady: false,
  allMessages: {
    en: {},
    fr: {},
    nl: {},
    es: {},
  },
  potentiallyUnusedTranslations: {
    en: {},
    fr: {},
    nl: {},
    es: {},
  },
  usedTranslations: {
    en: {},
    fr: {},
    nl: {},
    es: {},
  },
  missingTranslations: {
    en: {},
    fr: {},
    nl: {},
    es: {},
  },
};
const getters: GetterTree<TranslationReportState, RootState> = {
  allMessages: state => {
    return state.allMessages;
  },
  potentiallyUnusedTranslations: state => {
    return state.potentiallyUnusedTranslations;
  },
  usedTranslations: state => {
    return state.usedTranslations;
  },
  missingTranslations: state => {
    return state.missingTranslations;
  },
  usedTranslationRoutesByLocaleAndKey: state => (locale: string, key: string) => {
    return state.usedTranslations?.[locale]?.[key]?.routes || [];
  },
  // extends with new prop and methods here
};
const actions: ActionTree<TranslationReportState, RootState> = {
  // extends with new prop and methods here
};
const mutations: MutationTree<TranslationReportState> = {
  setUsedTranslations(
    state,
    payload: { locale: string; key: string; t: string; currentRoute: string },
  ) {
    if (!state.usedTranslations?.[payload.locale]?.[payload.key]) {
      Vue.set(state.usedTranslations[payload.locale], payload.key, {
        $t: payload.t,
        routes: [],
      });
    }
    const routes = state.usedTranslations?.[payload.locale]?.[payload.key]?.routes || [];
    if (!routes.includes(payload.currentRoute)) {
      routes.push(payload.currentRoute);
      Vue.set(state.usedTranslations[payload.locale][payload.key], 'routes', routes);
    }
  },
  setMissingTranslations(state, payload: { locale: string; key: string; currentRoute: string }) {
    if (!state.missingTranslations?.[payload.locale]?.[payload.key]) {
      Vue.set(state.missingTranslations[payload.locale], payload.key, {
        routes: [],
      });
    }
    const routes = state.missingTranslations?.[payload.locale]?.[payload.key]?.routes || [];
    if (!routes.includes(payload.currentRoute)) {
      routes.push(payload.currentRoute);
      Vue.set(state.missingTranslations[payload.locale][payload.key], 'routes', routes);
    }
  },
  removeFromPotentiallyUnusedTranslations(state, payload: { locale: string; key: string }) {
    Vue.delete(state.potentiallyUnusedTranslations[payload.locale], payload.key);
  },
  setMessages(state, messages) {
    Object.keys(messages).forEach(localeStr => {
      const dotNotationObject = convertToDotNotation(messages[localeStr]);
      Vue.set(state.allMessages, localeStr, dotNotationObject);
      Vue.set(state.usedTranslations, localeStr, {});
      Vue.set(state.missingTranslations, localeStr, {});
      Vue.set(state.potentiallyUnusedTranslations, localeStr, { ...dotNotationObject });
    });
    state.translationsReady = true;
  },
};

export const TranslationReportStoreModule: Module<TranslationReportState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
